import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Shield,
  Wifi,
  BarChart3,
  Check,
  Monitor,
  ArrowLeft,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { captureEvent } from "../lib/posthog";
import { cn } from "../lib/utils";

const features = [
  {
    icon: Monitor,
    title: "Design",
    description: "Modern & Mobile-First",
  },
  {
    icon: Shield,
    title: "Security",
    description: "Enterprise-Level Security & SSL",
  },
  {
    icon: Wifi,
    title: "Performance",
    description: "Fast Loading, Optimized for Speed",
  },
  {
    icon: BarChart3,
    title: "Growth",
    description: "SEO-Friendly • Google Business Profile • Lead Tracking",
  },
];

const inclusions = [
  "Business Domain & Email Setup",
  "Analytics Dashboard included",
  "Ongoing Updates & Support",
];

export default function Offer() {
  const location = useLocation();

  useEffect(() => {
    captureEvent("offer_page_viewed", {
      path: location.pathname,
      search: location.search || undefined,
    });
  }, [location.pathname, location.search]);

  const handleGetOfferClick = () => {
    captureEvent("offer_get_offer_clicked", { destination: "booking" });
  };

  return (
    <div className="min-h-screen bg-[#fafafa] text-gray-900">
      {/* Header - matches booking page */}
      <header className="border-b border-gray-200 bg-white pt-safe-top">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </Link>
          <div className="text-sm text-gray-500">Axios Development</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Left column - headline, pricing, CTA (Calendly-style sticky block) */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="lg:sticky lg:top-8">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">
                Professional Website System for Growing Businesses
              </h1>
              <p className="text-gray-500 text-sm sm:text-base mb-6">
                Built to Generate Leads. Not Just Look Good.
              </p>

              <div className="mb-6">
                <p className="text-gray-500 text-sm mb-1">From</p>
                <p className="text-2xl sm:text-3xl font-semibold text-gray-900">
                  <span className="text-[#0066ff]">$1,499</span>
                  <span className="text-gray-600 font-normal text-lg">
                    /year
                  </span>
                  <span className="text-gray-400 mx-1">or</span>
                  <span className="text-[#0066ff]">$220</span>
                  <span className="text-gray-600 font-normal text-lg">
                    /month
                  </span>
                </p>
              </div>

              <p className="text-gray-600 text-sm sm:text-base mb-6">
                We Handle the Tech. You Run the Business.
              </p>

<Link to="/booking?from=offer" className="block" onClick={handleGetOfferClick}>
              <Button
                size="lg"
                className={cn(
                  "w-full rounded-full bg-[#0066ff] hover:bg-[#0052cc] text-white px-6 py-6 text-base font-medium"
                )}
              >
                Get Offer
              </Button>
            </Link>
            </div>
          </div>

          {/* Right column - feature cards (same card style as booking) */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 px-6 py-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-[#0066ff]" />
                  What you get
                </h2>
              </div>
              <div className="p-6 sm:p-8">
                <div className="grid sm:grid-cols-2 gap-4">
                  {features.map(({ icon: Icon, title, description }) => (
                    <div
                      key={title}
                      className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors"
                    >
                      <div className="shrink-0 w-9 h-9 rounded-lg bg-[#0066ff]/10 flex items-center justify-center text-[#0066ff]">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-medium text-gray-900 text-sm">
                          {title}
                        </h3>
                        <p className="text-gray-500 text-sm mt-0.5">
                          {description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Inclusions - step-indicator style bar */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-500 mb-3">
                    Included
                  </p>
                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    {inclusions.map((item) => (
                      <div
                        key={item}
                        className="flex items-center gap-2 text-gray-700 text-sm"
                      >
                        <Check className="w-4 h-4 text-[#0066ff] shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
